/* ------------------------------------------------------------------
   قراردادِ RPC ِ چت ⟷ پنل — همکاران، «دیده شد» و ساختنِ گروه
   ------------------------------------------------------------------
   🔴 چرا RPC و نه خودِ API.

   رابطِ گفتگو یک Custom Script ِ ساده در قابِ Rocket.Chat است (دامنهٔ
   دیگر). نه کوکیِ نشستِ پنل را دارد و نه توکنِ CSRF؛ پس API را خودش
   صدا نمی‌زند و از پنجرهٔ والد می‌خواهد:

     چت ⟵ پنل   window.parent.postMessage({ carbonRpc: { id, method, params } }, PANEL_ORIGIN)
     پنل ⟵ چت   frame.postMessage({ carbonRpcResult: { id, ok: true,  data } }, CHAT_ORIGIN)
                frame.postMessage({ carbonRpcResult: { id, ok: false, error } }, CHAT_ORIGIN)
     پنل ⟵ چت   frame.postMessage({ carbonEvent: { name, data } }, CHAT_ORIGIN)     (هل‌داده)
     چت ⟵ پنل   window.parent.postMessage({ carbonActivity: 1 }, PANEL_ORIGIN)      (حضور — بی پاسخ)

   کلاینتِ آمادهٔ سمتِ چت: `infra/rocketchat/rpc-client.js`.

   ⚠️ پنل فقط وقتی پاسخ می‌دهد که پیام از مبدأِ چت و از همان قابِ خودش
   آمده باشد و کاربرِ قاب با ایمیل «تأییدشده» باشد (همان بررسیِ
   `ChatFrameHost`)؛ وگرنه `not_verified`. فقط جدولِ ثابتِ زیر — هیچ
   نشانی، فعل یا سرآیندی از قاب پذیرفته نمی‌شود. شکلِ `params` پیش از
   فراخوانی سنجیده می‌شود و سرور همهٔ مجوزها را دوباره می‌سنجد.

   ⚠️ این فایل «قرارداد» است: رابطِ چت دقیقاً روی همین نوشته می‌شود.
   نام و شکل را عوض نکنید؛ فقط چیز اضافه کنید و `RPC_PROTOCOL_VERSION`
   را بالا ببرید. REST ِ پشتِ هر متد در `docs/module-catalog/tasks.md`.
   ------------------------------------------------------------------ */

/**
 * با هر تغییرِ رفتار یکی بالا می‌رود؛ در `me.context` نیست — فقط برای خواننده.
 *
 * ۲ (‎2026-10-21، بازبینی): `Team.canManage` برای هر تیم جدا و `canAddMembers`
 * در تیم و پروژه؛ `permissions.manageAllTeams`؛ `MeContext.user` برای حسابِ
 * بی‌پرونده `null`؛ `limits.maxMembers`؛ ساعتِ بی‌منطقه در `dueAt` = وقتِ
 * تهران؛ `color: null` = رنگِ پیش‌فرض؛ کدهای خطای عضویت؛ پیامِ `carbonActivity`.
 *
 * ۳ (‎2026-10-21): `chat.seen` («دیده شد» ِ پیام‌های خودم — تیکِ تلگرامی)؛
 * `rooms.capabilities` و `rooms.create` (ساختنِ گروه و کانال از پنل). این سه
 * زیرِ `/api/v1/chat` اند، نه `/api/v1/tasks`.
 *
 * ۴ (۱۴۰۵/۰۷/۲۹): کار، تیم، پروژه و نمای کلی برداشته شدند (تصمیمِ مالک: کارها و
 * پروژه‌ها بعداً با اسکرامِ اوپن‌پروجکت داخلِ خودِ کاربن)؛ `me.context` فقط خودم،
 * `permissions.viewColleagues`، منطقهٔ زمانی، پنجرهٔ آنلاین و ساعتِ سرور است.
 * نسخهٔ ۳ در شاخهٔ `parked/tasks-module-1405-07` مانده است.
 *
 * ۵ (۱۴۰۵/۰۸/۱۱): `me.photoChanged` — «عکسِ نمایه‌ام را در گفتگو گذاشتم یا
 * برداشتم»؛ کاربن همان لحظه عکسِ گفتگو را عکسِ من می‌کند (یا به عکسِ پرسنلی
 * برمی‌گردد). `Person.photoUrl` از این پس عکسِ گفتگو است، وگرنه عکسِ پرسنلی.
 *
 * ۶ (۱۴۰۵/۰۸/۱۲): `Colleague.workMobile` (موبایلِ کاری، زیرِ نامِ کاربری در کارتِ نمایه) و
 * `me.mobile.requestCode` / `me.mobile.verify` — «اگه بخوان بتونن از همون صفحهٔ نمایه عوضش کنن»:
 * شمارهٔ خودم فقط با کدِ پیامکی که به شمارهٔ **تازه** رفته عوض می‌شود.
 */
export const RPC_PROTOCOL_VERSION = 6

/** کلاینت پس از این مدت با `timeout` رد می‌کند (پنل خودش سقف ندارد). */
export const RPC_TIMEOUT_MS = 8000

/** بیش از این درخواستِ هم‌زمان از یک قاب → `busy`. */
export const RPC_MAX_IN_FLIGHT = 16

/**
 * سقفِ «بقیهٔ اعضا» در `chat.seen` — همان `ChatRoomSeen::MAX_OTHERS` ِ سرور.
 * بیشتر ⟵ `bad_params` با `max`.
 */
export const RPC_MAX_SEEN_MEMBERS = 50

/** سقفِ اعضای `rooms.create` — همان `ChatRoomCreator::MAX_MEMBERS`. */
export const RPC_MAX_ROOM_MEMBERS = 100

/* ==================================================================
   شکل‌های پایه — همان `data` ِ REST، camelCase، بی‌تغییر
   ------------------------------------------------------------------
   · شناسه‌ها رشتهٔ UUID‌اند؛ «آدم» با `uuid` (شناسهٔ حساب) و بقیه با `id`.
   · زمان‌ها ISO-8601 به UTC (`2026-10-21T20:29:59Z`)؛ شمسی و وقتِ تهران
     فقط در نمایش.
   · رنگ‌ها همیشه `#RRGGBB`.
   ================================================================== */

export type Uuid = string
/** ISO-8601، UTC */
export type IsoDateTime = string
/** `#RRGGBB` */
export type HexColor = string

/**
 * یک همکار. `online` یعنی فعالیت روی **پنل** در `onlineWindowSeconds` ِ
 * گذشته (نه حضور در Rocket.Chat)؛ وگرنه `lastSeenAt` آخرین دیده‌شدن است.
 *
 * ⚠️ `photoUrl` از درگاهِ فایلِ پنل است: عکسی که شخص خودش در گفتگو گذاشته
 * (برای همهٔ بینندگانِ دفترچه دیدنی)، وگرنه عکسِ پرسنلی (که برای بیشترِ
 * بیننده‌ها ۴۰۳ می‌دهد). در چت آواتارِ خودِ Rocket.Chat (`/avatar/{chatUsername}`)
 * همان عکسِ گفتگو و امن‌تر است.
 * ⚠️ نام‌ها را فقط با `textContent` بنویسید، هرگز `innerHTML`.
 */
export interface Person {
  uuid: Uuid
  name: string
  /** نامِ کاربری در Rocket.Chat؛ `null` یعنی هنوز حسابِ گفتگو ندارد. */
  chatUsername: string | null
  photoUrl: string | null
  online: boolean
  lastSeenAt: IsoDateTime | null
}

export interface Colleague extends Person {
  jobTitle: string | null
  unit: string | null
  /**
   * موبایلِ کاری، یکسان‌شده (`09xxxxxxxxx`)؛ `null` یعنی ثبت نشده (۱۴۰۵/۰۸/۱۲، تصمیمِ مالک).
   * همان شمارهٔ ورودِ آن آدم است. ⚠️ فقط نشان‌دادن و کپی؛ تماس از اینجا هنوز نیست.
   */
  workMobile: string | null
  isMe: boolean
}

export interface MeContext {
  /** ⚠️ `null` برای حسابی که کارمند نیست (مثلاً ادمینِ سامانه): همکاران را می‌بیند، ولی خودش در فهرست نیست. */
  user: (Person & { jobTitle: string | null; unit: string | null }) | null
  /** فقط برای نشان‌دادن یا پنهان‌کردن؛ سرور هر درخواست را دوباره می‌سنجد. */
  permissions: {
    viewColleagues: boolean
  }
  /** `Asia/Tehran` */
  timezone: string
  /** پنجرهٔ «آنلاین» به ثانیه (۳۰۰). */
  onlineWindowSeconds: number
  serverTime: IsoDateTime
}

/* ------------------------------------------------------------------
   گفتگو (Rocket.Chat) — «دیده شد» و ساختنِ گروه و کانال
   ------------------------------------------------------------------ */

/** شناسهٔ اتاق یا کاربرِ Rocket.Chat — حروف و رقمِ لاتین، ۱ تا ۶۴ نویسه. */
export type ChatId = string

/** `p` گروهِ خصوصی · `c` کانالِ عمومی */
export type ChatRoomType = 'p' | 'c'

export const CHAT_ROOM_TYPES = ['p', 'c'] as const satisfies readonly ChatRoomType[]

export interface ChatSeen {
  rid: ChatId
  /**
   * تازه‌ترین زمانی که یکی از **بقیهٔ** اعضا اتاق را دید (`ls` ِ Rocket.Chat)؛
   * `null` = هنوز هیچ‌کس. هر پیامِ من با `ts` تا این لحظه «دیده شده» است —
   * در گروه یعنی دست‌کم یک نفر دید. میلی‌ثانیه دارد؛ گرد نکنید.
   */
  seenUntil: IsoDateTime | null
  /**
   * چند عضوِ دیگر سنجیده شد. صفر یعنی کسی نیست که ببیند (گفتگو با خودم، یا
   * گروهی که `members` برایش نیامد) — آن‌جا تیک نکشید.
   */
  others: number
}

export interface ChatRoomCapabilities {
  /** نقشی جز «کارمند» دارد — فقط برای نشان‌دادنِ دکمه؛ سرور هر ساختن را دوباره می‌سنجد. */
  canCreateRooms: boolean
}

/**
 * پاسخِ `me.photoChanged` — عکسی که کاربن حالا برای من نشان می‌دهد:
 * `chat` عکسی که در گفتگو گذاشتم · `personnel` عکسِ پرسنلی · `null` هیچ (حروفِ اول).
 */
export interface ChatPhotoState {
  shown: 'chat' | 'personnel' | null
}

/** پاسخِ `me.mobile.requestCode` — کد به همین شماره رفت. */
export interface MobileCodeSent {
  /** شمارهٔ تازه، یکسان‌شده (`09xxxxxxxxx`). */
  mobile: string
  /** عمرِ کد به ثانیه (۳۰۰). */
  expiresIn: number
  /** تا این چند ثانیه کدِ دوباره نمی‌رود (۶۰). */
  resendIn: number
}

/** پاسخِ `me.mobile.verify` — شماره‌ای که حالا روی پرونده و شناسهٔ ورود نشست. */
export interface MobileVerified {
  workMobile: string
}

export interface CreatedChatRoom {
  rid: ChatId
  /** نشانیِ اتاق: `/group/<name>` یا `/channel/<name>` (لاتین، ساختهٔ Rocket.Chat). */
  name: string
  type: ChatRoomType
  /** نامِ نمایشی — همان که کاربر نوشت. */
  displayName: string
}

/* ==================================================================
   ورودی‌ها
   ------------------------------------------------------------------
   · کلیدِ ناشناخته → `bad_params` (غلطِ تایپی بی‌صدا نمی‌ماند).
   · `null` برای فیلدی که پاک‌شدنی نیست همان «نیامدن» است.
   ================================================================== */

export type NoParams = Record<string, never>

/* ==================================================================
   جدولِ متدها — هر متد یک‌به‌یک یک مسیرِ REST: دو تا زیرِ `/api/v1/tasks`
   و چهار تا زیرِ `/api/v1/chat`
   ================================================================== */

export interface RpcMethods {
  /** GET /tasks/me — خودم و مجوزم (حسابِ بی‌پرونده هم: `user: null`). */
  'me.context': { params: NoParams; result: MeContext }

  /** GET /tasks/colleagues — همهٔ همکاران، آنلاین‌ها اول. `search` ≤ ۱۰۰ نویسه. */
  'colleagues.list': { params: { search?: string }; result: Colleague[] }

  /**
   * GET /chat/rooms/{rid}/seen — «دیده شد» ِ پیام‌های خودم در این اتاق.
   * · فقط عضوِ اتاق؛ غیرعضو همان `NOT_FOUND` ِ «اتاق نیست» را می‌گیرد.
   * · `members` فقط برای گروهِ خصوصی (‎/group/…): اعضایی که چت با نشستِ خودش دیده
   *   (‎`groups.members`، حداکثر ۵۰، بی خودم). Rocket.Chat ِ Community این فهرست را
   *   به سرور نمی‌دهد؛ سرور فقط کسانی را می‌شمارد که Rocket.Chat عضو بودنشان را تأیید کند.
   * · `after`: `ts` ِ تازه‌ترین پیامِ خودم — اگر از آخرین پرسشِ سرور تازه‌تر باشد،
   *   سرور زودتر از ۱۰ ثانیه دوباره می‌پرسد (نه زودتر از ۲ ثانیه).
   * · Rocket.Chat در دسترس نیست ⟵ `CHAT_UNAVAILABLE` (۵۰۳).
   */
  'chat.seen': { params: { rid: ChatId; members?: ChatId[]; after?: IsoDateTime }; result: ChatSeen }

  /** GET /chat/rooms/capabilities — آیا من گروه و کانال می‌سازم؟ */
  'rooms.capabilities': { params: NoParams; result: ChatRoomCapabilities }

  /**
   * POST /chat/rooms — ساختنِ گروه (`p`) یا کانال (`c`)؛ من مالکش می‌شوم.
   * · کارمندِ ساده ⟵ `CHAT_ROOM_CREATE_FORBIDDEN` (۴۰۳).
   * · `name` ≤ ۶۴ (فارسی هم؛ Rocket.Chat نشانیِ لاتین می‌سازد)، `description` ≤ ۵۰۰.
   * · `members` نامِ کاربریِ گفتگو (`Person.chatUsername`) — فقط همکارانِ دارای حسابِ
   *   گفتگو، ≤ ۱۰۰؛ ناشناخته ⟵ `VALIDATION_FAILED` با `members.N` / `not_found`.
   * · نامِ تکراری ⟵ `CHAT_ROOM_NAME_TAKEN` (۴۰۹)؛ حسابِ گفتگوی خودم جفت نشده ⟵
   *   `CHAT_ACCOUNT_NOT_LINKED` (۴۲۲).
   */
  'rooms.create': {
    params: { type: ChatRoomType; name: string; members?: string[]; description?: string | null }
    result: CreatedChatRoom
  }

  /**
   * POST /chat/me/photo — «عکسِ نمایه‌ام را در گفتگو گذاشتم یا برداشتم».
   * · بی ورودی: سرور عکسِ گفتگوی **خودِ** کاربرِ پنل را از Rocket.Chat می‌خواند؛
   *   هیچ عکس یا شناسه‌ای از قاب پذیرفته نمی‌شود.
   * · پس از نشستنِ ETag ِ تازه صدا بزنید (`/api/v1/me` در چت عوض شده باشد)؛ وگرنه
   *   سرور هنوز عکسِ قبلی را می‌بیند و تورِ پنج‌دقیقه‌ایِ سرور جبرانش می‌کند.
   * · حسابِ گفتگو جفت نشده ⟵ `CHAT_ACCOUNT_NOT_LINKED` (۴۲۲)؛ Rocket.Chat پاسخ
   *   نداد ⟵ `CHAT_UNAVAILABLE` (۵۰۳)؛ بیش از ۶ بار در دقیقه ⟵ `RATE_LIMITED`.
   */
  'me.photoChanged': { params: NoParams; result: ChatPhotoState }

  /**
   * POST /me/mobile/request-code — کدِ ۶ رقمی به شمارهٔ **تازهٔ** خودم (پیامک).
   * · فقط خودم؛ هیچ ورودی‌ای آدمِ دیگری را نمی‌گوید.
   * · شمارهٔ نادرست ⟵ `VALIDATION_FAILED` (`mobile` / `invalid`)؛ همان شمارهٔ فعلی ⟵
   *   `PEOPLE_MOBILE_UNCHANGED`؛ شمارهٔ کسِ دیگری ⟵ `PEOPLE_MOBILE_TAKEN` (۴۰۹)؛ حسابِ بی‌پرونده ⟵
   *   `PEOPLE_MOBILE_NO_PROFILE`؛ دقیقه‌ای یک کد و روزی ۸ ⟵ `RATE_LIMITED`؛ پیامک نرفت ⟵
   *   `PEOPLE_SMS_UNAVAILABLE` (۵۰۳).
   */
  'me.mobile.requestCode': { params: { mobile: string }; result: MobileCodeSent }

  /**
   * POST /me/mobile/verify — کدِ همان شماره؛ درست بود ⟵ پرونده و شناسهٔ ورود همان لحظه.
   * · کد اشتباه ⟵ `PEOPLE_MOBILE_CODE_INVALID`؛ منقضی یا باطل ⟵ `PEOPLE_MOBILE_CODE_EXPIRED`؛
   *   ۵ بار اشتباه ⟵ `PEOPLE_MOBILE_CODE_ATTEMPTS` (کدِ تازه لازم است).
   */
  'me.mobile.verify': { params: { mobile: string; code: string }; result: MobileVerified }
}

export type RpcMethod = keyof RpcMethods
export type RpcParams<M extends RpcMethod> = RpcMethods[M]['params']
export type RpcResult<M extends RpcMethod> = RpcMethods[M]['result']

const METHOD_SET = {
  'me.context': 1,
  'colleagues.list': 1,
  'chat.seen': 1,
  'rooms.capabilities': 1,
  'rooms.create': 1,
  'me.photoChanged': 1,
  'me.mobile.requestCode': 1,
  'me.mobile.verify': 1,
} as const satisfies Record<RpcMethod, 1>

/** همهٔ متدها — هر نامِ دیگری `unknown_method` است. */
export const RPC_METHODS = Object.keys(METHOD_SET) as RpcMethod[]

export function isRpcMethod(name: unknown): name is RpcMethod {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(METHOD_SET, name)
}

/* ==================================================================
   خطاها
   ------------------------------------------------------------------
   `error.message` همیشه فارسی و نشان‌دادنی است.
   · کدهای حروف‌کوچک مالِ پل‌اند (پنل یا کلاینت)؛
   · کدهای حروف‌بزرگ همان `error.code` ِ سرور (پاکتِ PRD §15.1).
   ================================================================== */

export const RPC_BRIDGE_ERRORS = {
  /** کاربرِ قاب هنوز (یا دیگر) همان کاربرِ پنل شناخته نشده — پس از `session.verified` دوباره. */
  not_verified: 'not_verified',
  /** نامِ متد در جدول نیست. */
  unknown_method: 'unknown_method',
  /** شکلِ `params` درست نیست؛ `fields` می‌گوید کجا (کدها: required · type · uuid · format · max · min · in · unknown). */
  bad_params: 'bad_params',
  /** بیش از `RPC_MAX_IN_FLIGHT` درخواستِ باز. */
  busy: 'busy',
  /** (کلاینت) چت داخلِ پنل نیست. */
  not_framed: 'not_framed',
  /** (کلاینت) پاسخی در `RPC_TIMEOUT_MS` نرسید. */
  timeout: 'timeout',
  /** (کلاینت) postMessage شکست خورد. */
  post_failed: 'post_failed',
  /** خطای پیش‌بینی‌نشده در خودِ پل. */
  internal: 'internal',
} as const

export type RpcBridgeErrorCode = keyof typeof RPC_BRIDGE_ERRORS

export const RPC_SERVER_ERRORS = [
  'VALIDATION_FAILED', // ۴۲۲ — `fields` دارد
  'FORBIDDEN', // ۴۰۳
  'NOT_FOUND', // ۴۰۴ — نبودن یا ندیدن، یکی است
  'UNAUTHENTICATED', // ۴۰۱ — نشستِ پنل تمام شد؛ پنل خودش به صفحهٔ ورود می‌رود
  'RATE_LIMITED', // ۴۲۹
  'NETWORK_ERROR', // پنل به سرور نرسید
  'INTERNAL_ERROR',
  'MAINTENANCE',
  'CHAT_UNAVAILABLE', // ۵۰۳ — Rocket.Chat پاسخ نداد؛ کمی بعد دوباره
  'CHAT_ROOM_CREATE_FORBIDDEN', // ۴۰۳ — کارمندِ ساده گروه و کانال نمی‌سازد
  'CHAT_ROOM_NAME_TAKEN', // ۴۰۹ — گروه یا کانالی با همین نام هست
  'CHAT_ACCOUNT_NOT_LINKED', // ۴۲۲ — حسابِ گفتگوی خودم هنوز با پنل جفت نشده
] as const

export type RpcServerErrorCode = (typeof RPC_SERVER_ERRORS)[number]

export interface RpcFieldError {
  /** نامِ کلید در `params` (مثلاً `title`، `assignee.uuid`، `members.2`) */
  field: string
  code: string
  message?: string
}

export interface RpcError {
  /** `RpcBridgeErrorCode` یا `RpcServerErrorCode` — کدِ ناشناخته را «خطای عمومی» بخوانید. */
  code: RpcBridgeErrorCode | RpcServerErrorCode | (string & {})
  message: string
  /** وضعیتِ HTTP ِ سرور؛ برای خطاهای پل نیست. */
  status?: number
  fields?: RpcFieldError[]
  /** شناسهٔ درخواستِ سرور — برای گزارشِ خطا به پشتیبانی. */
  requestId?: string
}

/* ==================================================================
   رویدادها (پنل ⟵ چت) — `{ carbonEvent: { name, data } }`
   ================================================================== */

export interface RpcEvents {
  /** کاربرِ قاب همین الان تأیید شد — درخواست‌های `not_verified` را دوباره بفرستید. */
  'session.verified': Record<string, never>
}

export type RpcEventName = keyof RpcEvents

export const RPC_EVENTS = ['session.verified'] as const satisfies readonly RpcEventName[]

/* ==================================================================
   پاکت‌ها
   ================================================================== */

/** شناسهٔ درخواست: ۱ تا ۶۴ نویسه از `[A-Za-z0-9._:-]`؛ وگرنه پیام نادیده گرفته می‌شود. */
export const RPC_ID_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/

export interface RpcRequest<M extends RpcMethod = RpcMethod> {
  id: string
  method: M
  params?: RpcParams<M>
}

export type RpcReply<M extends RpcMethod = RpcMethod> =
  | { id: string; ok: true; data: RpcResult<M> }
  | { id: string; ok: false; error: RpcError }

export type RpcEvent<N extends RpcEventName = RpcEventName> = { name: N; data: RpcEvents[N] }

export interface RpcRequestMessage {
  carbonRpc: RpcRequest
}

export interface RpcReplyMessage {
  carbonRpcResult: RpcReply
}

export interface RpcEventMessage {
  carbonEvent: RpcEvent
}

/**
 * (چت ⟵ پنل) «کاربر همین الان در قاب کار کرد» — بی پاسخ.
 *
 * 🔴 «آنلاین» یعنی کار با پنل در چند دقیقهٔ گذشته؛ کلیک و کلیدِ داخلِ قاب را
 * پنل خودش نمی‌بیند. اسکریپتِ چت با هر کارِ واقعی (کلیک، کلید، چرخ، لمس)
 * حداکثر هر ۳۰ ثانیه یک بار این را به `window.parent` می‌فرستد
 * (`rpc-client.js` همین کار را می‌کند). همان دروازه‌های RPC: از همین قاب و
 * با کاربرِ تأییدشده؛ وگرنه نادیده.
 */
export interface RpcActivityMessage {
  carbonActivity: 1
}
